import React, { useMemo, useState } from 'react';
import { Loader2, Search, SearchX, X } from 'lucide-react';
import GameTile from './GameTile';
import ProviderPicker from './ProviderPicker';
import { mapForTile } from '../hooks/useGameCatalog';
import { resolveApiAssetUrl } from '../lib/api';
import { normalizeProviderName } from '../lib/providerLogos';
import { useActiveTheme } from '../lib/activeTheme';
import { HOOKS, hook } from '../lib/domHooks';

// Hazır şemaların ızgarası kendi referans arayüzlerinden gelir: Merit'te yedi,
// Zümrüt'te ve Paris'te sekiz sütun; varsayılan şemada altı sütun kalır.
const DEFAULT_GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4';
const MERIT_GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2.5 md:gap-3';
// Betrun katalogu telefonda ana sayfa şeridi gibi üçlü dizilir: iki sütunda
// kartlar büyük kalıyor, oyuncu bir ekranda çok az oyun görüyordu.
const BETRUN_GRID = 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-3';
const ZUMRUT_GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5 md:gap-3';
const PARIS_GRID = 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5 md:gap-3';
const HERKUL_GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2.5 md:gap-3';
// Referans katalogda satır başına yedi kart düşer (`calc(14.2857% - 15px)`).
const VEGA_GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-3';
const THEME_GRIDS = {
  merit: MERIT_GRID, zumrut: ZUMRUT_GRID, paris: PARIS_GRID, herkul: HERKUL_GRID,
  vega: VEGA_GRID, betrun: BETRUN_GRID,
};

// Katalog binlerce oyuna ulaştığında sağlayıcı şeridi yüzü aşabiliyor; yatay
// kaydırmayla taranamaz hâle geldiği için arama kutusu çizilir. Kısa listede
// kutu yalnızca yer kaplar, bu yüzden eşiğin altında hiç gösterilmez.
const PROVIDER_SEARCH_MIN = 8;

/**
 * Slot / Canlı Casino / Crash sayfalarının ortak gövdesi: arama kutusu,
 * sekme ve sağlayıcı çipleri, oyun ızgarası ve "daha fazla" sayfalaması.
 *
 * ``renderTile`` verilirse kart görünümü sayfaya özel hâle getirilebilir
 * (ör. canlı masalarda CANLI rozeti).
 */
const GameCatalog = ({
  catalog,
  chips = [],
  chipValue,
  onChipChange,
  searchPlaceholder = 'Oyun ara...',
  emptyText = 'Aramanıza uygun oyun bulunamadı.',
  gridClassName,
  renderTile,
}) => {
  const {
    items, total, hasMore, loading, loadingMore, error,
    providers, provider, setProvider, q, setQ, loadMore,
  } = catalog;
  const themeId = useActiveTheme();
  const [providerQuery, setProviderQuery] = useState('');
  // Eşleştirme sağlayıcı adının yazım farklarından arındırılmış hâli üzerinden
  // yapılır: oyuncu "pragmatic play" yazdığında "PragmaticPlay" da bulunur.
  const providerTerm = normalizeProviderName(providerQuery);
  const visibleProviders = useMemo(() => {
    if (!providerTerm) return providers;
    return providers.filter((item) => (
      // Seçili sağlayıcı arama sonucunda kalmasa da listede kalır; aksi hâlde
      // oyuncu hangi süzgecin açık olduğunu göremez ve kapatamaz.
      item.slug === provider
      || normalizeProviderName(item.name).includes(providerTerm)
      || normalizeProviderName(item.slug).includes(providerTerm)
    ));
  }, [provider, providerTerm, providers]);
  // Sayfa kendi ızgarasını verdiyse ona dokunulmaz (ör. canlı masaların
  // dikey kartları); vermediyse şemanın varsayılanı kullanılır.
  const grid = gridClassName || THEME_GRIDS[themeId] || DEFAULT_GRID;

  return (
    <>
      <div className="pusu-panel catalog-toolbar p-4 md:p-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        {/* Oyun araması ile sağlayıcı süzgeci tek satırı paylaşır: ikisi de
            "hangi oyunlar listelensin" sorusunu sorar ve ayrı bantlara
            konduklarında dar ekranda ızgarayı ekranın dışına itiyorlardı.
            Sağlayıcı şeridi (çipler) aşağıda kalır — orası süzgeç değil,
            markaların vitrinidir. */}
        <div className="catalog-toolbar-fields flex flex-1 min-w-0 gap-3">
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)] z-10" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pusu-input pl-10"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>
          {/* Süzgeç yerel `<select>` değil logolu seçicidir
              (components/ProviderPicker.jsx): select'in açılır listesi işletim
              sisteminin çizdiği düz yazıdır, logo taşıyamaz ve araması yoktur
              — yüz küsur sağlayıcıda oyuncu markayı adından bulmak zorunda
              kalıyordu. Bir süre yalnızca bir şemada çiziliyordu; çip şeridi
              logoları gösterse de select orada da logosuzdu ve şeridi hiç
              çizmeyen şemada (Vega) tek yol odur. */}
          {providers.length > 0 && (
            <div className="catalog-toolbar-provider relative min-w-0">
              <ProviderPicker providers={providers} value={provider} onChange={setProvider} />
            </div>
          )}
        </div>
        {chips.length > 0 && (
          <div className="catalog-chips flex flex-wrap gap-2">
            {chips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => onChipChange(chip.id)}
                className={`provider-chip flex items-center gap-1.5 ${chipValue === chip.id ? 'active' : ''}`}
              >
                {chip.icon && <chip.icon size={12} />} {chip.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {providers.length > 0 && (
        <div className="catalog-providers mt-4">
          {/* Filtre ve arama çiplerle aynı satırda, şeridin sağ ucunda durur ve
              kaydırma kabının **dışındadır**: içine konsalardı çipler
              kaydırıldığında görünürden çıkar, tam da kullanılırken kaybolurlardı. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex gap-2 overflow-x-auto pb-2 flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setProvider('all')}
                className={`provider-chip whitespace-nowrap ${provider === 'all' ? 'active' : ''}`}
              >
                Tüm Sağlayıcılar
              </button>
              {visibleProviders.map((item) => (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => setProvider(item.slug)}
                  className={`provider-chip whitespace-nowrap ${provider === item.slug ? 'active' : ''}`}
                  title={item.name}
                >
                  {/* Logo tanımlıysa ad yerine görsel; panelde her sağlayıcı için
                      ayrı ayarlanır (Admin → Oyunlar → Sağlayıcı Görünümü). */}
                  {item.logo ? (
                    <img
                      src={resolveApiAssetUrl(item.logo)}
                      alt={item.name}
                      className="h-5 max-w-[92px] w-auto object-contain"
                      loading="lazy"
                    />
                  ) : item.name}
                </button>
              ))}
            </div>
            <div className="flex w-full gap-2 pb-2 sm:w-auto">
              {providers.length >= PROVIDER_SEARCH_MIN && (
                <div className="provider-search relative min-w-0 flex-1 sm:w-52 sm:flex-none">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)] z-10 pointer-events-none" />
                  <input
                    value={providerQuery}
                    onChange={(e) => setProviderQuery(e.target.value)}
                    className="pusu-input provider-search-input pl-10 pr-9"
                    placeholder={`Sağlayıcı ara (${providers.length})`}
                    aria-label="Sağlayıcı ara"
                    autoComplete="off"
                  />
                  {providerQuery && (
                    <button
                      type="button"
                      onClick={() => setProviderQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white z-10"
                      aria-label="Sağlayıcı aramasını temizle"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {providerTerm && visibleProviders.length === 0 && (
            <div className="text-sm text-gray-400 pb-2">
              “{providerQuery.trim()}” ile eşleşen sağlayıcı bulunamadı.
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-6 pusu-card p-4 text-sm text-red-300 border border-red-500/25">{error}</div>
      )}

      {loading ? (
        <div className={`mt-6 ${grid}`} aria-label="Oyunlar yükleniyor">
          {Array.from({ length: 12 }, (_, i) => <div key={i} className="home-game-skeleton" />)}
        </div>
      ) : (
        <>
          <div className="mt-6 text-sm text-gray-400">{total} oyun bulundu</div>
          {items.length === 0 ? (
            <div className="mt-6 pusu-card p-10 text-center text-gray-400">
              <SearchX size={32} className="mx-auto mb-3 text-[var(--pusu-gold)]" />
              {emptyText}
            </div>
          ) : (
            <div {...hook(HOOKS.GAME_GRID)} className={`mt-3 ${grid}`}>
              {items.map((item) => (
                renderTile
                  ? renderTile(item)
                  : <GameTile key={item.id} game={mapForTile(item)} onPlay={catalog.onPlay} />
              ))}
            </div>
          )}

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="gold-outline-btn h-11 px-8 rounded-xl text-sm disabled:opacity-60"
              >
                {loadingMore
                  ? <><Loader2 size={15} className="inline animate-spin mr-2" /> Yükleniyor...</>
                  : `Daha Fazla Göster (${items.length}/${total})`}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default GameCatalog;
